import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppWidget from '../../components/feature/WhatsAppWidget';
import CookieConsent from '../../components/feature/CookieConsent';
import { updateSEO, getBaseUrl } from '../../utils/seo';

const ServicesPage = () => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    updateSEO({
      title: 'Hizmetlerimiz | Bursa Sulama Sistemleri - Yağmurlama, Damlama, Otomatik Sulama',
      description: 'Bursa\'da profesyonel sulama sistemleri kurulumu: Yağmurlama sistemleri, sprey sprink, damlama sulama, depo ve hidrofor kurulumları, tamir ve tadilat hizmetleri. Ücretsiz keşif ve danışmanlık.',
      keywords: 'bursa yağmurlama sistemleri, damlama sulama kurulumu, otomatik sulama sistemleri, hidrofor kurulumu bursa, sulama sistemi tamiri, sprey sprink sistemleri',
      canonical: `${baseUrl}/hizmetler`,
      ogImage: 'https://public.readdy.ai/ai/img_res/6bd1a11883a714fe30c39c04a450595a.jpg',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Sulama Sistemleri Kurulumu ve Bakımı',
        provider: {
          '@type': 'LocalBusiness',
          name: 'Bursa Sulama Sistemleri',
          telephone: '+905071344668',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Atatürk Caddesi No:72, Hasan Boztürk Apartmanı',
            addressLocality: 'Görükle, Nilüfer',
            addressRegion: 'Bursa',
            addressCountry: 'TR'
          }
        },
        areaServed: {
          '@type': 'City',
          name: 'Bursa'
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Sulama Sistemleri Hizmetleri',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Yağmurlama Sistemleri',
                description: 'Geniş alanlarda homojen ve etkili sulama sağlayan yağmurlama sistemleri kurulumu'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Sprey Sprink Sistemleri',
                description: 'Hassas sulama gerektiren alanlar için özel sprey sistemleri'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Damlama Sistemleri',
                description: 'Su tasarrufu sağlayan, verimli ve ekonomik damlama sulama sistemleri'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Depo ve Hidrofor Kurulumları',
                description: 'Profesyonel su depolama ve basınçlandırma sistemleri kurulumu'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Otomatik Sulama Sistemleri Tamir ve Tadilatı',
                description: 'Mevcut sulama sistemlerinin bakım, onarım ve iyileştirme hizmetleri'
              }
            }
          ]
        }
      }
    });
  }, []);

  const services = [
    {
      title: 'Sprey Sprink Sistemleri',
      description:
        'Hassas sulama gerektiren alanlar için özel olarak tasarlanmış sprey sistemleri. Çiçek bahçeleri, peyzaj alanları ve küçük yeşil alanlar için mükemmel çözüm.',
      features: [
        'Ayarlanabilir sprey başlıkları',
        'Düşük basınçta çalışma',
        'Hassas sulama kontrolü',
        'Çeşitli sprey desenleri',
      ],
      icon: 'ri-contrast-drop-line',
      image:
        'https://readdy.ai/api/search-image?query=modern%20spray%20sprinkler%20irrigation%20nozzle%20spraying%20fine%20water%20mist%20on%20colorful%20flowers%20and%20plants%20in%20garden%2C%20precision%20watering%20system%20with%20visible%20water%20spray%20pattern%20creating%20rainbow%20effect%2C%20clean%20simple%20light%20background%2C%20professional%20equipment%20photography&width=600&height=400&seq=service-detail-2&orientation=landscape',
    },
    {
      title: 'Yağmurlama Sistemleri',
      description:
        'Geniş alanlarda homojen ve etkili sulama sağlayan yağmurlama sistemleri kurulumu. Bahçeler, parklar, spor sahaları ve tarımsal alanlar için ideal çözümler sunuyoruz.',
      features: [
        'Pop-up sprinkler sistemleri',
        'Rotor tipi yağmurlama başlıkları',
        'Otomatik kontrol sistemleri',
        'Su tasarrufu sağlayan akıllı programlama',
      ],
      icon: 'ri-water-flash-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20lawn%20sprinkler%20irrigation%20system%20spraying%20water%20on%20green%20grass%20in%20a%20large%20garden%2C%20modern%20automatic%20watering%20equipment%20with%20multiple%20sprinkler%20heads%20visible%2C%20water%20droplets%20sparkling%20in%20sunlight%2C%20clean%20simple%20light%20background%20highlighting%20the%20irrigation%20system%2C%20high%20quality%20commercial%20photography&width=600&height=400&seq=service-detail-1&orientation=landscape',
    },
    {
      title: 'Damlama Sistemleri',
      description:
        'Su tasarrufu sağlayan, verimli ve ekonomik damlama sulama sistemleri. Tarımsal üretim, sera sulama ve özel bitki yetiştirme için en uygun çözüm.',
      features: [
        'Yüzey ve yeraltı damlama sistemleri',
        'Ayarlanabilir damlatıcılar',
        '%50\'ye varan su tasarrufu',
        'Gübre karıştırma sistemleri',
      ],
      icon: 'ri-drop-line',
      image:
        'https://readdy.ai/api/search-image?query=drip%20irrigation%20system%20with%20water%20droplets%20on%20black%20tubing%20near%20plant%20roots%20in%20rich%20soil%2C%20efficient%20water%20saving%20agriculture%20technology%20close%20up%20view%2C%20healthy%20green%20plants%20growing%2C%20clean%20simple%20light%20background%2C%20professional%20agricultural%20photography&width=600&height=400&seq=service-detail-3&orientation=landscape',
    },
    {
      title: 'Otomatik Sulama Sistemleri Tamir ve Tadilatı',
      description:
        'Mevcut sulama sistemlerinizin bakım, onarım ve iyileştirme hizmetleri. Arızalı sistemlerin tamiri ve performans artırıcı güncellemeler.',
      features: [
        'Arıza tespit ve onarım',
        'Sistem performans analizi',
        'Parça değişimi ve yenileme',
        'Sistem optimizasyonu',
      ],
      icon: 'ri-tools-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20technician%20repairing%20irrigation%20system%20equipment%20with%20tools%2C%20maintenance%20work%20on%20sprinkler%20system%20components%20and%20valves%2C%20hands%20working%20on%20water%20pipes%20with%20wrench%2C%20clean%20outdoor%20setting%20with%20simple%20light%20background%2C%20professional%20service%20photography&width=600&height=400&seq=service-detail-5&orientation=landscape',
    },
    {
      title: 'Depo ve Hidrofor Kurulumları',
      description:
        'Profesyonel su depolama ve basınçlandırma sistemleri kurulumu. Kesintisiz sulama için gerekli altyapı çözümleri sunuyoruz.',
      features: [
        'Polietilen ve paslanmaz çelik depolar',
        'Otomatik hidrofor sistemleri',
        'Basınç kontrol sistemleri',
        'Filtre ve arıtma sistemleri',
      ],
      icon: 'ri-database-2-line',
      image:
        'https://readdy.ai/api/search-image?query=modern%20water%20storage%20tank%20and%20hydrophore%20pump%20system%20installation%2C%20professional%20plumbing%20equipment%20with%20large%20blue%20water%20tank%20and%20pressure%20pump%20unit%2C%20clean%20industrial%20setting%20with%20pipes%20and%20valves%2C%20simple%20light%20background%2C%20high%20quality%20technical%20photography&width=600&height=400&seq=service-detail-4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-[#0A1628] mb-4 md:mb-6">
              Hizmetlerimiz
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-gray-600 leading-relaxed px-4">
              Profesyonel sulama sistemleri kurulumu, bakım ve onarım
              hizmetleri
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-12 md:py-20 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="space-y-12 md:space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 1, y: 0 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#14B8A6] rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                    <i className={`${service.icon} text-3xl md:text-4xl text-white`}></i>
                  </div>
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 md:mb-6">
                    {service.title}
                  </h2>
                  <p className="text-base md:text-xl text-gray-600 leading-relaxed mb-6 md:mb-8">
                    {service.description}
                  </p>
                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-xl md:text-2xl text-[#14B8A6] flex-shrink-0 mt-1"></i>
                        <span className="text-sm md:text-lg text-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="tel:+905071344668"
                    className="inline-flex items-center gap-3 bg-[#14B8A6] hover:bg-[#0F9B8E] text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <i className="ri-phone-fill text-lg md:text-xl"></i>
                    <span>Fiyat Teklifi Al</span>
                  </a>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-[#F8F6F3]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 md:mb-6 px-4">
              Çalışma Sürecimiz
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Profesyonel ve sistematik yaklaşımımızla projelerinizi hayata
              geçiriyoruz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {[
              {
                step: '01',
                title: 'Keşif ve Analiz',
                description:
                  'Alanınızı inceliyor, ihtiyaçlarınızı belirliyoruz',
              },
              {
                step: '02',
                title: 'Proje Tasarımı',
                description:
                  'Size özel sulama sistemi projesi hazırlıyoruz',
              },
              {
                step: '03',
                title: 'Kurulum',
                description:
                  'Profesyonel ekibimizle sistemi kuruyoruz',
              },
              {
                step: '04',
                title: 'Bakım ve Destek',
                description:
                  '7/24 teknik destek ve periyodik bakım hizmeti',
              },
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg"
              >
                <div className="text-4xl md:text-6xl font-bold text-[#14B8A6]/20 mb-3 md:mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-[#0A1628] mb-2 md:mb-3">
                  {process.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 px-4">
              Ücretsiz Keşif ve Danışmanlık
            </h2>
            <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
              Projeleriniz için ücretsiz keşif yapıyor ve en uygun çözümü
              sunuyoruz
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
              <a
                href="tel:+905071344668"
                className="inline-flex items-center gap-3 bg-white text-[#0A1628] px-8 md:px-10 py-4 md:py-5 rounded-full text-lg md:text-xl font-semibold hover:scale-105 transition-all duration-300 shadow-2xl whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl md:text-2xl"></i>
                <span>0507 134 46 68</span>
              </a>
              <a
                href="https://wa.me/905071344668?text=Merhaba,%20ben%20sizinle%20bursasulamasistemleri.com%20sitesinden%20iletişime%20geçiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 md:px-10 py-4 md:py-5 rounded-full text-lg md:text-xl font-semibold hover:scale-105 transition-all duration-300 shadow-2xl whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill text-xl md:text-2xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      <CookieConsent />
    </div>
  );
};

export default ServicesPage;
